import { ReactElement, useState, useCallback, useEffect, useMemo } from 'react';
import { DialogTitle, DialogContentText, Button, Link } from '@mui/material';
import { LoadingButton } from '@mui/lab';
import { useFormik, FormikProvider, FormikHelpers } from 'formik';
import { useSnackbar } from 'notistack';

import { FileUploadV2, ImporterProgress } from '~components/index';
import {
  useLazyRequest,
  useImporterOrdersProgressListener,
  useSampleTemplates,
} from '~hooks/index';
import { importItemsFromCSV } from '~services/item';
import { ItemsImporter } from '~services/item/types';
import { getImporterOrderFromCSVAlertDescription } from '~utils/order';
import { toAbsoluteUrl } from '~utils/assetsHelpers';

import { DialogImportOrdersFromCSVProps } from './types';
import {
  DialogImportOrdersFromCSVContainer,
  DialogImportOrdersFromCSVContent,
  DialogImportOrdersFromCSVActions,
} from './styles';
import { FIELDS_NAME, initialValues, validationSchema } from './utils';

const defaultImportedItemsData: ItemsImporter = {
  errorRows: 0,
  importedRows: 0,
  message: '',
  totalRows: 0,
};

const DialogImportOrdersFromCSV = ({
  open,
  onClose,
  onSubmitSuccess,
  onUpdateErrors,
}: DialogImportOrdersFromCSVProps): ReactElement => {
  const { enqueueSnackbar } = useSnackbar();

  const { items: dowloadTemplateUrl } = useSampleTemplates();

  const [runImporterProgressListener, setImporterProgressListener] =
    useState(false);
  const [isCompletedRequest, setIsCompletedRequest] = useState(false);
  const [importedItemsData, setImportedItemsData] = useState<ItemsImporter>(
    defaultImportedItemsData,
  );

  const {
    status,
    updateImporterStatus,
    data: { progress, step, total },
    reset: resetImporter,
  } = useImporterOrdersProgressListener({
    runListenerUpdates: open && runImporterProgressListener,
    onCompleted: () => {
      setImporterProgressListener(false);

      if (importedItemsData.errorRows < 1) {
        setTimeout(() => {
          onClose();
        }, 1500);
      }
    },
  });

  const loading = useMemo(() => status === 'inProgress', [status]);

  const isImporting = useMemo(
    () => loading || status === 'completed' || runImporterProgressListener,
    [loading, status, runImporterProgressListener],
  );

  const [, , , executeImportItems] = useLazyRequest({
    request: importItemsFromCSV,
    withPostSuccess: (response) => {
      const responseImporter = response.data?.data as ItemsImporter;

      setImportedItemsData(responseImporter);

      setIsCompletedRequest(true);
      onSubmitSuccess();
    },
    withPostFailure: () => {
      setIsCompletedRequest(true);
      enqueueSnackbar('Ha ocurrido un error, intente nuevamente', {
        variant: 'error',
      });
    },
  });

  const isCompletedProcessImporter = useMemo(
    () => status === 'completed' && isCompletedRequest,
    [status, isCompletedRequest],
  );

  const importerProgressTitle = useMemo(
    () =>
      isCompletedProcessImporter
        ? 'Archivo importado'
        : 'Importando desde archivo',
    [isCompletedProcessImporter],
  );

  const importerProgressDescription = useMemo(
    () =>
      isCompletedProcessImporter
        ? getImporterOrderFromCSVAlertDescription(importedItemsData)
        : `Importando ${step} de ${total}`,
    [isCompletedProcessImporter, importedItemsData, step, total],
  );

  const onSubmit = useCallback(
    async (
      values: typeof initialValues,
      { setSubmitting }: FormikHelpers<typeof initialValues>,
    ) => {
      setImporterProgressListener(true);
      updateImporterStatus('inProgress');
      setIsCompletedRequest(false);
      await executeImportItems(values[FIELDS_NAME.FILES]);

      setSubmitting(false);
    },
    [updateImporterStatus, executeImportItems],
  );

  const formikBag = useFormik({
    initialValues,
    validationSchema,
    onSubmit,
  });

  const { resetForm, submitForm, isSubmitting, dirty, isValid, values } =
    formikBag;

  useEffect(() => {
    if (!open) {
      setTimeout(() => {
        resetForm();
        resetImporter();
        setImporterProgressListener(false);
        setIsCompletedRequest(false);
        setImportedItemsData(defaultImportedItemsData);
      }, 500);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open]);

  const disabledSubmit = useMemo(
    () => !dirty || !isValid || !values[FIELDS_NAME.FILES],
    [dirty, isValid, values],
  );

  return (
    <FormikProvider value={formikBag}>
      <DialogImportOrdersFromCSVContainer
        open={open}
        onClose={onClose}
        maxWidth="sm"
      >
        <DialogTitle>Importar Pedidos</DialogTitle>

        <DialogImportOrdersFromCSVContent dividers>
          {!isImporting && (
            <DialogContentText color="text.primary">
              Importar pedidos desde un archivo de forma masiva
            </DialogContentText>
          )}

          <FileUploadV2
            name={FIELDS_NAME.FILES}
            labelImg={{
              src: toAbsoluteUrl('/images/import_file.png'),
              alt: 'Import File',
            }}
            accept=".csv, .xls, .xlsx"
            disabled={isImporting}
          />

          {!isImporting && (
            <DialogContentText variant="caption">
              Si deseas importar de forma masiva utilizando un archivo, puedes{' '}
              <Link
                href={dowloadTemplateUrl}
                download
                color="primary"
                underline="hover"
                fontWeight="bold"
              >
                descargar y modificar la plantilla de ejemplo.
              </Link>{' '}
              Si necesitas información consulta la{' '}
              <Link
                href="https://routix.tawk.help/article/cargar-pedidos-de-forma-masiva"
                download
                color="primary"
                underline="hover"
                fontWeight="bold"
              >
                ayuda.
              </Link>{' '}
            </DialogContentText>
          )}

          {isImporting && (
            <ImporterProgress
              title={importerProgressTitle}
              description={importerProgressDescription}
              progress={progress}
              warning={importedItemsData.errorRows >= 1}
            />
          )}
        </DialogImportOrdersFromCSVContent>

        <DialogImportOrdersFromCSVActions>
          <Button color="secondary" onClick={onClose}>
            Cerrar
          </Button>

          {!isImporting && (
            <LoadingButton
              variant="contained"
              color="primary"
              onClick={submitForm}
              loading={loading || isSubmitting}
              disabled={disabledSubmit}
            >
              Importar
            </LoadingButton>
          )}

          {isCompletedProcessImporter && importedItemsData.errorRows >= 1 && (
            <Button
              variant="contained"
              color="primary"
              onClick={onUpdateErrors}
            >
              Corregir Errores
            </Button>
          )}
        </DialogImportOrdersFromCSVActions>
      </DialogImportOrdersFromCSVContainer>
    </FormikProvider>
  );
};

export default DialogImportOrdersFromCSV;
